import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { Ruler3D } from '../decor/Ruler3D'
import { FeatureDispatch } from '../features/FeatureDispatch'
import {
  radialConeGeometry,
  radianSphereGeometry,
} from '@asset-viz/utils/geometryDefines'
import { extractScale, Vec3 } from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import { GeckoAssetModel, GeckoEllipsoidalHead, GeckoMeasurement } from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing an ellipsoidal head - an elliptical end cap
 * for pressure vessels with a 2:1 elliptical ratio. This provides
 * better stress distribution than hemispherical heads while being
 * less complex than torispherical heads.
 *
 * @param model       The ellipsoidal head component model
 * @param assetModel  The asset model containing this head
 * @param groupProps  Position, rotation, scale, and other 3D properties
 */
export const EllipsoidalHeadSurface3D: React.FC<
  {
    model: GeckoEllipsoidalHead
    assetModel?: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const plates = model.platesArray
  const nozzles = model.nozzlesArray
  const circumferentialWelds = model.circumferentialWeldsArray
  const radialWelds = model.radialWeldsArray

  const { bounds, scale } = React.useMemo(() => {
    const bounds = model.componentBounds

    // Ellipsoidal heads use radius and minor radius for elliptical scaling
    // Create elliptical shape with 2:1 ratio (radius in X/Z, minor radius in Y)
    const scale = extractScale(groupProps.scale).multiply(
      new Vector3(
        model.radius.num, // Width (X-axis)
        show3D ? model.minorRadius.num : model.radius.num, // Height (Y-axis) - half of radius for 2:1 ratio
        show3D ? model.radius.num : 1.0 // Depth (Z-axis)
      )
    )

    return {
      bounds,
      scale,
    }
  }, [model, groupProps.scale, show3D])

  const geometry = React.useMemo(() => {
    if (!show3D) return radialConeGeometry
    // Use sphere geometry but scale it elliptically to create 2:1 ellipsoid
    return radianSphereGeometry
  }, [show3D])

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      if (!show3D) {
        const radius = 1.05
        const radialXPosition = radius * -Math.sin((coord.num * Math.PI) / 180)
        const radialYPosition = radius * Math.cos((coord.num * Math.PI) / 180)
        return [radialXPosition, radialYPosition, 0]
      }
      const radius = 1.1
      const radialXPosition = radius * -Math.cos((coord.num * Math.PI) / 180)
      const radialZPosition = radius * Math.sin((coord.num * Math.PI) / 180)
      return [radialXPosition, 0, radialZPosition]
    },
    [show3D]
  )

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}>
      {/* Render ellipsoidal head features using regular plates for elliptical geometry */}
      <Ruler3D
        anchor='center'
        start={bounds.start.x}
        end={bounds.end.x}
        includeTicks={true}
        includeStart={true}
        includeEnd={false}
        getTickPosition={positionXTick}
      />
      <FeatureDispatch
        features={[plates, nozzles, circumferentialWelds, radialWelds]}
      />
    </ModelSurface3D>
  )
}
